import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import CloseIcon from '@mui/icons-material/Close';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';

import useDraggablePanel from '../../../../hooks/useDraggablePanel';
import topicGroupSearchEngine from './TopicGroupSearchEngine';
import useTopicGroupSearch from './useTopicGroupSearch';
import TopicGroupSearchResults from './TopicGroupSearchResults';
import './TopicGroupSearchDialog.css';

const DEFAULT_POS = () => ({ top: 120, left: Math.max(8, window.innerWidth / 2 - 240) });

/**
 * Floating draggable search dialog scoped to an active TopicGroup service.
 * Rendered via portal. Follows the GroupSettingsDlg reference architecture:
 * useDraggablePanel for drag mechanics, window-level keyboard handling, id'd containers.
 * @param {{ open: boolean, onClose: () => void, service: string }} props
 */
export default function TopicGroupSearchDialog({ open, onClose, service }) {
    const { results, loading, query } = useTopicGroupSearch();
    const { pos, dragHandleProps } = useDraggablePanel('TopicGroupSearchDialog', DEFAULT_POS());

    useEffect(() => {
        if (!open) return;
        topicGroupSearchEngine.bootup();
        topicGroupSearchEngine.setService(service);
    }, [open, service]);

    const handleClose = () => {
        topicGroupSearchEngine.setService(null);
        onClose();
    };

    const handleQueryChange = (e) => {
        topicGroupSearchEngine.search(e.target.value);
    };

    useEffect(() => {
        if (!open) return;
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') handleClose();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [open]);

    if (!open) return null;

    const panel = (
        <div id="topic-group-search-panel" className="tgs-dialog" style={{ top: pos.top, left: pos.left }}>
            <div id="topic-group-search-titlebar" className="tgs-titlebar" {...dragHandleProps}>
                <ManageSearchIcon sx={{ fontSize: 13, color: '#a3d1ff', mr: '6px' }} />
                <span>Search · {service}</span>
                <div className="tgs-titlebar-actions">
                    <CloseIcon sx={{ fontSize: 16, cursor: 'pointer', color: '#a3d1ff' }} onClick={handleClose} />
                </div>
            </div>

            <div id="topic-group-search-input-row" className="tgs-search-row">
                <input
                    className="tgs-input"
                    type="text"
                    placeholder="Search notes..."
                    value={query}
                    onChange={handleQueryChange}
                    autoFocus
                />
            </div>

            <div id="topic-group-search-results" className="tgs-results-area">
                <TopicGroupSearchResults results={results} loading={loading} query={query} />
            </div>

            <div id="topic-group-search-statusbar" className="tgs-statusbar">
                {query.trim().length >= 3 ? `${results.length} found` : ''}
            </div>
        </div>
    );

    return createPortal(panel, document.body);
}
